import React, { useState } from 'react';
import {
  FileText, Code, Server, Download, ChevronRight, ChevronDown,
  Star, Building2, Globe, Database, Shield, Mail,
  CreditCard, Users, Map, BarChart3, Camera, Calculator, Bell,
  Copy, CheckCircle, BookOpen, FileSignature, Key, Zap,
  Clock, RefreshCw, Link as LinkIcon
} from 'lucide-react';

// ============================================
// FEATURE REGISTRY - Single source of truth
// Updated: December 2025
// ============================================
const FEATURES = [
  // SOLO TIER
  {
    id: 'customers',
    name: 'Customer Management',
    description: 'Unlimited customers with full contact info, addresses, and notes',
    tier: 'Solo',
    category: 'Core',
    icon: Users,
    releaseDate: '2025-01-01',
    apiAvailable: true,
  },
  {
    id: 'jobs',
    name: 'Job Management',
    description: 'Create, schedule, and track jobs with multiple services per job',
    tier: 'Solo',
    category: 'Core',
    icon: FileText,
    releaseDate: '2025-01-01',
    apiAvailable: true,
  },
  {
    id: 'invoicing',
    name: 'Invoicing & Payments',
    description: 'Professional invoices with online payment links via Stripe Connect',
    tier: 'Solo',
    category: 'Core',
    icon: CreditCard,
    releaseDate: '2025-01-01',
    apiAvailable: false,
  },
  {
    id: 'invoice-status',
    name: 'Auto Invoice Status',
    description: 'Automatic status updates (Pending → Overdue) with daily Hangfire job',
    tier: 'Solo',
    category: 'Core',
    icon: RefreshCw,
    releaseDate: '2025-12-27',
    apiAvailable: false,
  },
  {
    id: 'expenses',
    name: 'Expense Tracking',
    description: 'Track business expenses with categories and receipt attachments',
    tier: 'Solo',
    category: 'Core',
    icon: Calculator,
    releaseDate: '2025-01-01',
    apiAvailable: false,
  },
  {
    id: 'leads',
    name: 'Lead Management',
    description: 'Capture and convert leads from website forms with QR codes',
    tier: 'Solo',
    category: 'Core',
    icon: Users,
    releaseDate: '2025-01-01',
    apiAvailable: false,
  },
  {
    id: 'suddysnap',
    name: 'SuddySnap™ Receipt Scanner',
    description: 'AI-powered receipt scanning using Azure Document Intelligence',
    tier: 'Solo',
    category: 'Tools',
    icon: Camera,
    releaseDate: '2025-06-01',
    apiAvailable: false,
  },
  {
    id: 'quote-calculator',
    name: 'Quote Calculator',
    description: 'Configurable pricing with tiers, modifiers, minimums, and quote breakdowns',
    tier: 'Solo',
    category: 'Tools',
    icon: Calculator,
    releaseDate: '2025-03-01',
    apiAvailable: false,
  },
  {
    id: 'schedule-c',
    name: 'Schedule C Tax Reports',
    description: 'Generate IRS Schedule C reports for tax filing',
    tier: 'Solo',
    category: 'Reports',
    icon: FileText,
    releaseDate: '2025-02-01',
    apiAvailable: false,
  },
  {
    id: 'quarterly-tax',
    name: 'Quarterly Tax Estimates',
    description: 'Calculate estimated quarterly tax payments',
    tier: 'Solo',
    category: 'Reports',
    icon: Calculator,
    releaseDate: '2025-02-01',
    apiAvailable: false,
  },
  {
    id: 'basic-emails',
    name: 'Essential Emails',
    description: 'Job confirmation, invoice sent, payment received via Azure Communication Services',
    tier: 'Solo',
    category: 'Communications',
    icon: Mail,
    releaseDate: '2025-01-01',
    apiAvailable: false,
  },
  {
    id: 'payment-reminders',
    name: 'Payment Reminders',
    description: 'Automated 3/7/14-day overdue payment reminders with smart scheduling',
    tier: 'Solo',
    category: 'Communications',
    icon: Bell,
    releaseDate: '2025-12-27',
    apiAvailable: false,
  },
  {
    id: 'unsubscribe-system',
    name: 'Email Unsubscribe',
    description: 'CAN-SPAM compliant unsubscribe with category preferences',
    tier: 'Solo',
    category: 'Communications',
    icon: Mail,
    releaseDate: '2025-12-27',
    apiAvailable: false,
  },
  {
    id: 'service-agreements',
    name: 'Service Agreements',
    description: 'Send digital agreements with e-signature capture and tracking',
    tier: 'Solo',
    category: 'Communications',
    icon: FileSignature,
    releaseDate: '2025-12-01',
    apiAvailable: false,
  },
  // PRO TIER
  {
    id: 'team-members',
    name: 'Team Members',
    description: 'Add up to 15 team members with role-based access',
    tier: 'Pro',
    category: 'Team',
    icon: Users,
    releaseDate: '2025-04-01',
    apiAvailable: false,
  },
  {
    id: 'route-optimization',
    name: 'SuddyRoute™ Optimization',
    description: 'AI-powered route optimization prioritizing revenue per hour over distance',
    tier: 'Pro',
    category: 'Operations',
    icon: Map,
    releaseDate: '2025-05-01',
    apiAvailable: false,
  },
  {
    id: 'automated-emails',
    name: 'Automated Emails',
    description: 'Scheduled reminders, follow-ups, and review requests via Hangfire',
    tier: 'Pro',
    category: 'Communications',
    icon: Bell,
    releaseDate: '2025-07-01',
    apiAvailable: false,
  },
  {
    id: 'customer-portal',
    name: 'Customer Portal',
    description: 'Self-service portal for customers to view jobs and pay invoices',
    tier: 'Pro',
    category: 'Customer Experience',
    icon: Globe,
    releaseDate: '2025-08-01',
    apiAvailable: false,
  },
  {
    id: 'pnl-reports',
    name: 'P&L Financial Reports',
    description: 'Profit & Loss statements with period comparisons',
    tier: 'Pro',
    category: 'Reports',
    icon: BarChart3,
    releaseDate: '2025-03-01',
    apiAvailable: false,
  },
  {
    id: 'mfa',
    name: 'Two-Factor Authentication',
    description: 'TOTP-based MFA for enhanced account security',
    tier: 'Pro',
    category: 'Security',
    icon: Shield,
    releaseDate: '2025-09-01',
    apiAvailable: false,
  },
  {
    id: 'quickbooks-sync',
    name: 'QuickBooks Integration',
    description: 'OAuth sync: customers, invoices, payments, services to QuickBooks Online',
    tier: 'Pro',
    category: 'Integrations',
    icon: BookOpen,
    releaseDate: '2025-12-24',
    apiAvailable: false,
  },
  {
    id: 'custom-agreement-templates',
    name: 'Custom Agreement Templates',
    description: 'Copy and customize service agreement templates with merge fields',
    tier: 'Pro',
    category: 'Communications',
    icon: FileSignature,
    releaseDate: '2025-12-01',
    apiAvailable: false,
  },
  // ENTERPRISE TIER
  {
    id: 'api-access',
    name: 'REST API Access',
    description: 'Full API access for external integrations with scoped keys',
    tier: 'Enterprise',
    category: 'Integrations',
    icon: Code,
    releaseDate: '2025-12-22',
    apiAvailable: true,
  },
  {
    id: 'unlimited-team',
    name: 'Unlimited Team Members',
    description: 'No limit on team member accounts',
    tier: 'Enterprise',
    category: 'Team',
    icon: Users,
    releaseDate: '2025-04-01',
    apiAvailable: false,
  },
  {
    id: 'custom-email-templates',
    name: 'Custom Email Templates',
    description: 'Fully customizable email templates with merge fields and branding',
    tier: 'Enterprise',
    category: 'Communications',
    icon: Mail,
    releaseDate: '2025-10-01',
    apiAvailable: false,
  },
  {
    id: 'custom-branding',
    name: 'Custom Branding',
    description: 'Your logo in emails, customer portal, and public forms',
    tier: 'Enterprise',
    category: 'Customization',
    icon: Building2,
    releaseDate: '2025-11-01',
    apiAvailable: false,
  },
  {
    id: 'create-agreement-templates',
    name: 'Create Agreement Templates',
    description: 'Build custom agreement templates from scratch',
    tier: 'Enterprise',
    category: 'Communications',
    icon: FileSignature,
    releaseDate: '2025-12-01',
    apiAvailable: false,
  },
  {
    id: 're-engagement',
    name: 'Re-engagement Campaigns',
    description: 'Automated campaigns for 90/180/365 day inactive customers',
    tier: 'Enterprise',
    category: 'Marketing',
    icon: Bell,
    releaseDate: '2025-01-01',
    status: 'planned',
    apiAvailable: false,
  },
];

// ============================================
// API ENDPOINTS REGISTRY
// ============================================
const API_ENDPOINTS = [
  {
    category: 'Customers',
    endpoints: [
      { method: 'GET', path: '/api/v1/customers', description: 'List all customers', scopes: ['customers:read'] },
      { method: 'GET', path: '/api/v1/customers/{id}', description: 'Get customer by ID', scopes: ['customers:read'] },
      { method: 'GET', path: '/api/v1/customers/search?q=', description: 'Search customers', scopes: ['customers:read'] },
      { method: 'POST', path: '/api/v1/customers', description: 'Create customer', scopes: ['customers:write'] },
      { method: 'PUT', path: '/api/v1/customers/{id}', description: 'Update customer', scopes: ['customers:write'] },
    ]
  },
  {
    category: 'Jobs',
    endpoints: [
      { method: 'GET', path: '/api/v1/jobs', description: 'List all jobs (with filters)', scopes: ['jobs:read'] },
      { method: 'GET', path: '/api/v1/jobs/{id}', description: 'Get job by ID', scopes: ['jobs:read'] },
      { method: 'GET', path: '/api/v1/jobs/customer/{customerId}', description: 'Get jobs by customer', scopes: ['jobs:read'] },
      { method: 'POST', path: '/api/v1/jobs', description: 'Create job', scopes: ['jobs:write'] },
      { method: 'PATCH', path: '/api/v1/jobs/{id}/status', description: 'Update job status', scopes: ['jobs:write'] },
    ]
  },
  {
    category: 'Service Types',
    endpoints: [
      { method: 'GET', path: '/api/v1/servicetypes', description: 'List all service types', scopes: ['servicetypes:read'] },
      { method: 'GET', path: '/api/v1/servicetypes/{id}', description: 'Get service type by ID', scopes: ['servicetypes:read'] },
    ]
  },
  {
    category: 'Invoices',
    endpoints: [
      { method: 'GET', path: '/api/v1/invoices', description: 'List all invoices', scopes: ['invoices:read'] },
      { method: 'GET', path: '/api/v1/invoices/{id}', description: 'Get invoice by ID', scopes: ['invoices:read'] },
      { method: 'GET', path: '/api/v1/invoices/customer/{customerId}', description: 'Get invoices by customer', scopes: ['invoices:read'] },
    ]
  },
];

// ============================================
// ARCHITECTURE INFO - Updated December 2025
// ============================================
const ARCHITECTURE = {
  stack: [
    { name: 'Frontend', tech: 'React 18 + Vite + TailwindCSS', icon: Globe },
    { name: 'Backend', tech: 'ASP.NET Core 8 Web API', icon: Server },
    { name: 'Database', tech: 'Azure SQL (Basic tier)', icon: Database },
    { name: 'Authentication', tech: 'JWT + TOTP MFA', icon: Shield },
    { name: 'Email', tech: 'Azure Communication Services', icon: Mail },
    { name: 'Storage', tech: 'Azure Blob Storage', icon: Database },
    { name: 'AI/ML', tech: 'Azure Document Intelligence', icon: Camera },
    { name: 'Payments', tech: 'Stripe Connect (0.5% platform fee)', icon: CreditCard },
    { name: 'Background Jobs', tech: 'Hangfire (SQL Storage)', icon: Clock },
    { name: 'PDF Generation', tech: 'DinkToPdf (wkhtmltopdf)', icon: FileText },
    { name: 'Accounting', tech: 'QuickBooks Online API (OAuth 2.0)', icon: BookOpen },
  ],
  hangfireJobs: [
    { name: 'ProcessDueEmailsJob', schedule: 'Daily 8 AM UTC', description: 'Process scheduled emails from EmailSchedules table' },
    { name: 'UpdateOverdueInvoicesJob', schedule: 'Daily midnight UTC', description: 'Update invoice status to Overdue when past due' },
    { name: 'RefreshExpiringTokensJob', schedule: 'Daily 2 AM UTC', description: 'Refresh QuickBooks OAuth tokens before expiry' },
    { name: 'UpdateExpiredConnectionsJob', schedule: 'Daily 3 AM UTC', description: 'Mark expired QuickBooks connections' },
  ],
  azureResources: [
    { name: 'suddyflow-api', type: 'App Service', location: 'East US 2' },
    { name: 'suddyflow-frontend', type: 'Static Web App', location: 'East US 2' },
    { name: 'suddyflow-db', type: 'SQL Database (Basic $5/mo)', location: 'East US' },
    { name: 'suddyflow-sql', type: 'SQL Server', location: 'East US' },
    { name: 'suddyflowstorage', type: 'Storage Account', location: 'East US 2' },
    { name: 'suddyflow-email-services', type: 'Communication Service', location: 'Global' },
    { name: 'suddyflow-doc-intel', type: 'Document Intelligence', location: 'East US 2' },
  ],
  integrations: [
    { name: 'Stripe Connect', description: 'Payment processing, subscriptions, 0.5% platform fee', status: 'active' },
    { name: 'QuickBooks Online', description: 'Customers, invoices, payments, services sync', status: 'active' },
    { name: 'Google Maps', description: 'Route optimization and geocoding', status: 'active' },
    { name: 'Azure Doc Intelligence', description: 'Receipt OCR scanning (SuddySnap)', status: 'active' },
    { name: 'Azure Communication Services', description: 'Transactional emails with templates', status: 'active' },
  ],
  pricing: {
    solo: { price: 19, name: 'Solo' },
    pro: { price: 49, name: 'Pro' },
    enterprise: { price: 129, name: 'Enterprise' },
  }
};

// ============================================
// DATABASE TABLES - Updated December 2025
// ============================================
const DATABASE_TABLES = [
  { name: 'Businesses', description: 'Multi-tenant business accounts', records: '~10' },
  { name: 'Users', description: 'User accounts with roles', records: '~30' },
  { name: 'Customers', description: 'Customer records per business', records: '~500' },
  { name: 'Jobs', description: 'Service jobs with scheduling', records: '~1,000' },
  { name: 'JobServices', description: 'Line items per job', records: '~2,000' },
  { name: 'ServiceTypes', description: 'Configurable service offerings', records: '~50' },
  { name: 'Invoices', description: 'Generated invoices with status tracking', records: '~800' },
  { name: 'Payments', description: 'Payment records', records: '~600' },
  { name: 'Expenses', description: 'Business expense tracking', records: '~200' },
  { name: 'Leads', description: 'Sales leads with source tracking', records: '~100' },
  { name: 'QuickBooksConnections', description: 'OAuth connections to QB', records: '~5' },
  { name: 'QuickBooksSyncLogs', description: 'Sync history and errors', records: '~500' },
  { name: 'ServiceAgreements', description: 'Digital agreements sent', records: '~50' },
  { name: 'ServiceAgreementTemplates', description: 'Agreement templates (system + custom)', records: '~10' },
  { name: 'AgreementSignatures', description: 'E-signature records with IP tracking', records: '~30' },
  { name: 'ApiKeys', description: 'API keys for external access', records: '~5' },
  { name: 'EmailTemplates', description: 'Email templates (system + custom)', records: '~30' },
  { name: 'EmailSchedules', description: 'Scheduled emails with idempotency', records: '~500' },
  { name: 'EmailLogs', description: 'Sent email history', records: '~2,000' },
  { name: 'CustomerEmailPreferences', description: 'Unsubscribe preferences by category', records: '~100' },
  { name: 'BusinessEmailSettings', description: 'Email automation settings', records: '~10' },
];

const AdminDocumentation = () => {
  const [activeTab, setActiveTab] = useState('features');
  const [expandedCategories, setExpandedCategories] = useState(['Core', 'Customers', 'Jobs']);
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const tabs = [
    { id: 'features', name: 'Features', icon: Star },
    { id: 'api', name: 'API', icon: Code },
    { id: 'architecture', name: 'Stack', icon: Server },
    { id: 'database', name: 'DB', icon: Database },
    { id: 'export', name: 'Export', icon: Download },
  ];

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(id);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Solo': return 'bg-gray-100 text-gray-700';
      case 'Pro': return 'bg-blue-100 text-blue-700';
      case 'Enterprise': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-green-100 text-green-700';
      case 'POST': return 'bg-blue-100 text-blue-700';
      case 'PUT': return 'bg-amber-100 text-amber-700';
      case 'PATCH': return 'bg-orange-100 text-orange-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Group features by category
  const featuresByCategory = FEATURES.reduce((acc, feature) => {
    if (!acc[feature.category]) acc[feature.category] = [];
    acc[feature.category].push(feature);
    return acc;
  }, {});

  // Export functions
  const exportAsMarkdown = () => {
    let md = `# SuddyFlow Platform Documentation\n\n`;
    md += `Generated: ${new Date().toISOString()}\n\n`;
    
    md += `## Features (${FEATURES.length} total)\n\n`;
    md += `| Feature | Tier | Category | API |\n`;
    md += `|---------|------|----------|-----|\n`;
    FEATURES.forEach(f => {
      md += `| ${f.name} | ${f.tier} | ${f.category} | ${f.apiAvailable ? '✅' : '❌'} |\n`;
    });
    
    md += `\n## API Endpoints\n\n`;
    API_ENDPOINTS.forEach(cat => {
      md += `### ${cat.category}\n\n`;
      md += `| Method | Endpoint | Description |\n`;
      md += `|--------|----------|-------------|\n`;
      cat.endpoints.forEach(ep => {
        md += `| ${ep.method} | \`${ep.path}\` | ${ep.description} |\n`;
      });
      md += `\n`;
    });
    
    md += `## Tech Stack\n\n`;
    ARCHITECTURE.stack.forEach(s => {
      md += `- **${s.name}**: ${s.tech}\n`;
    });

    md += `\n## Hangfire Background Jobs\n\n`;
    ARCHITECTURE.hangfireJobs.forEach(j => {
      md += `- **${j.name}** (${j.schedule}): ${j.description}\n`;
    });
    
    md += `\n## Azure Resources\n\n`;
    ARCHITECTURE.azureResources.forEach(r => {
      md += `- **${r.name}**: ${r.type} (${r.location})\n`;
    });

    md += `\n## Third-Party Integrations\n\n`;
    ARCHITECTURE.integrations.forEach(i => {
      md += `- **${i.name}**: ${i.description}\n`;
    });

    md += `\n## Database Tables (${DATABASE_TABLES.length})\n\n`;
    md += `| Table | Description |\n`;
    md += `|-------|-------------|\n`;
    DATABASE_TABLES.forEach(t => {
      md += `| ${t.name} | ${t.description} |\n`;
    });
    
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suddyflow-docs-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsJSON = () => {
    const data = {
      exportDate: new Date().toISOString(),
      features: FEATURES,
      apiEndpoints: API_ENDPOINTS,
      architecture: ARCHITECTURE,
      databaseTables: DATABASE_TABLES,
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suddyflow-docs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Platform Documentation</h1>
        <p className="text-sm text-gray-600">Feature inventory, API reference, and architecture</p>
      </div>

      {/* Tabs - Horizontal scroll on mobile */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={16} />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Features Tab */}
      {activeTab === 'features' && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
              <p className="text-xs text-gray-600 font-medium">Solo</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {FEATURES.filter(f => f.tier === 'Solo').length}
              </p>
              <p className="text-xs text-gray-500">$19/mo</p>
            </div>
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <p className="text-xs text-blue-600 font-medium">Pro</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900">
                {FEATURES.filter(f => f.tier === 'Pro').length}
              </p>
              <p className="text-xs text-blue-500">$49/mo</p>
            </div>
            <div className="card p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <p className="text-xs text-purple-600 font-medium">Enterprise</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-900">
                {FEATURES.filter(f => f.tier === 'Enterprise').length}
              </p>
              <p className="text-xs text-purple-500">$129/mo</p>
            </div>
          </div>

          {/* Features by Category */}
          {Object.entries(featuresByCategory).map(([category, features]) => (
            <div key={category} className="card overflow-hidden">
              <button
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 text-sm sm:text-base">{category}</span>
                  <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                    {features.length}
                  </span>
                </div>
                {expandedCategories.includes(category) ? (
                  <ChevronDown size={18} className="text-gray-400" />
                ) : (
                  <ChevronRight size={18} className="text-gray-400" />
                )}
              </button>
              
              {expandedCategories.includes(category) && (
                <div className="divide-y divide-gray-100">
                  {features.map((feature) => (
                    <div key={feature.id} className="p-3 sm:p-4 flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        feature.tier === 'Solo' ? 'bg-gray-100' :
                        feature.tier === 'Pro' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        <feature.icon size={14} className={
                          feature.tier === 'Solo' ? 'text-gray-600' :
                          feature.tier === 'Pro' ? 'text-blue-600' : 'text-purple-600'
                        } />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 mb-1">
                          <span className="font-medium text-gray-900 text-sm">{feature.name}</span>
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getTierColor(feature.tier)}`}>
                            {feature.tier}
                          </span>
                          {feature.apiAvailable && (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              API
                            </span>
                          )}
                          {feature.status === 'planned' && (
                            <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                              Planned
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* API Reference Tab */}
      {activeTab === 'api' && (
        <div className="space-y-4">
          {/* Authentication Info */}
          <div className="card p-3 sm:p-4 bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2 text-sm">Authentication</h3>
            <p className="text-xs sm:text-sm text-blue-700 mb-2">
              API requests require <code className="bg-blue-100 px-1 rounded text-xs">X-API-Key</code> header.
            </p>
            <pre className="bg-blue-900 text-blue-100 p-2 rounded text-xs overflow-x-auto">
{`curl -H "X-API-Key: sf_live_xxx" \\
  https://api.suddyflow.com/api/v1/customers`}
            </pre>
          </div>

          {/* Endpoints */}
          {API_ENDPOINTS.map((category) => (
            <div key={category.category} className="card overflow-hidden">
              <button
                onClick={() => toggleCategory(category.category)}
                className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 text-sm">{category.category}</span>
                  <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
                    {category.endpoints.length}
                  </span>
                </div>
                {expandedCategories.includes(category.category) ? (
                  <ChevronDown size={18} className="text-gray-400" />
                ) : (
                  <ChevronRight size={18} className="text-gray-400" />
                )}
              </button>
              
              {expandedCategories.includes(category.category) && (
                <div className="divide-y divide-gray-100">
                  {category.endpoints.map((endpoint, idx) => (
                    <div key={idx} className="p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 text-xs font-mono font-bold rounded ${getMethodColor(endpoint.method)}`}>
                          {endpoint.method}
                        </span>
                        <code className="text-xs text-gray-700 flex-1 truncate">{endpoint.path}</code>
                        <button
                          onClick={() => copyToClipboard(endpoint.path, `${category.category}-${idx}`)}
                          className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                          {copiedEndpoint === `${category.category}-${idx}` ? (
                            <CheckCircle size={14} className="text-green-500" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-600">{endpoint.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Architecture Tab */}
      {activeTab === 'architecture' && (
        <div className="space-y-4">
          {/* Tech Stack */}
          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">Tech Stack</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {ARCHITECTURE.stack.map((item) => (
                <div key={item.name} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <item.icon size={14} className="text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-xs">{item.name}</p>
                    <p className="text-xs text-gray-500 truncate">{item.tech}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Hangfire Jobs */}
          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
              <Clock size={16} className="text-orange-500" />
              Background Jobs (Hangfire)
            </h3>
            <div className="space-y-2">
              {ARCHITECTURE.hangfireJobs.map((job) => (
                <div key={job.name} className="p-2 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 text-xs">{job.name}</span>
                    <span className="text-xs text-orange-600">{job.schedule}</span>
                  </div>
                  <p className="text-xs text-gray-600">{job.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Integrations */}
          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">Integrations</h3>
            <div className="space-y-2">
              {ARCHITECTURE.integrations.map((integration) => (
                <div key={integration.name} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-xs">{integration.name}</p>
                    <p className="text-xs text-gray-500 truncate">{integration.description}</p>
                  </div>
                  <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 flex-shrink-0 ml-2">
                    {integration.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">Pricing</h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-lg font-bold text-gray-900">${ARCHITECTURE.pricing.solo.price}</p>
                <p className="text-xs text-gray-600">{ARCHITECTURE.pricing.solo.name}/mo</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-lg font-bold text-blue-900">${ARCHITECTURE.pricing.pro.price}</p>
                <p className="text-xs text-blue-600">{ARCHITECTURE.pricing.pro.name}/mo</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg text-center">
                <p className="text-lg font-bold text-purple-900">${ARCHITECTURE.pricing.enterprise.price}</p>
                <p className="text-xs text-purple-600">{ARCHITECTURE.pricing.enterprise.name}/mo</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Database Tab */}
      {activeTab === 'database' && (
        <div className="space-y-4">
          <div className="card p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-3 text-sm">
              Database Schema ({DATABASE_TABLES.length} Tables)
            </h3>
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Table</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {DATABASE_TABLES.map((table) => (
                    <tr key={table.name} className="border-b border-gray-100">
                      <td className="py-2 px-3 font-mono text-xs text-gray-900">{table.name}</td>
                      <td className="py-2 px-3 text-gray-600 text-xs">{table.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card p-3 bg-blue-50 border-blue-200">
            <h4 className="font-medium text-blue-900 mb-1 text-sm">Multi-Tenant Architecture</h4>
            <p className="text-xs text-blue-700">
              All data isolated using <code className="bg-blue-100 px-1 rounded">BusinessId</code> foreign keys.
            </p>
          </div>
        </div>
      )}

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-gray-900 mb-2 text-sm">Export Documentation</h3>
            <p className="text-xs text-gray-600 mb-4">
              Download for due diligence, handoffs, or archival.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={exportAsMarkdown}
                className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
              >
                <FileText size={18} className="text-gray-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm">Markdown</p>
                  <p className="text-xs text-gray-500">.md file</p>
                </div>
              </button>
              
              <button
                onClick={exportAsJSON}
                className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left"
              >
                <Code size={18} className="text-gray-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 text-sm">JSON</p>
                  <p className="text-xs text-gray-500">.json file</p>
                </div>
              </button>
            </div>
          </div>

          <div className="card p-3 bg-amber-50 border-amber-200">
            <h4 className="font-medium text-amber-900 mb-1 text-sm">📋 Export Contents</h4>
            <ul className="text-xs text-amber-700 space-y-0.5">
              <li>• {FEATURES.length} features with tier assignments</li>
              <li>• All API endpoints with scopes</li>
              <li>• Tech stack and Azure resources</li>
              <li>• {DATABASE_TABLES.length} database tables</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDocumentation;